import { useState, useEffect } from 'react';

export const MOBILE_BREAKPOINT = 640;

/**
 * Detects iPad reliably across iPadOS versions.
 *
 * iPadOS 13+ changed the default Safari UA to match Mac desktop:
 *   "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15..."
 * The only reliable distinguisher from a real Mac is maxTouchPoints > 1.
 *
 * Legacy iPads (iPadOS ≤ 12) still include 'iPad' in the UA string.
 */
function detectIsIPad(): boolean {
  if (typeof navigator === 'undefined') return false;
  // Legacy iPadOS ≤ 12
  if (/iPad/.test(navigator.userAgent)) return true;
  // iPadOS 13+ masquerades as Mac — maxTouchPoints > 1 distinguishes it
  if (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1) return true;
  return false;
}

function checkIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  // iPads always get the desktop layout regardless of viewport dimensions
  if (detectIsIPad()) return false;
  const w = window.innerWidth;
  const h = window.innerHeight;
  // Portrait phone: width <= 640px
  if (w <= MOBILE_BREAKPOINT) return true;
  // Landscape phone: wider than tall but narrow enough to be a phone.
  // iPad portrait (820×1180) has h > 500 so it won't match here.
  // iPads are already excluded above, but the h < 500 guard is a safety net.
  if (w > h && w < 900 && h < 500) return true;
  return false;
}

/** Exported for use where callers need to adapt behaviour specifically for iPad */
export function detectIPad(): boolean {
  return detectIsIPad();
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => checkIsMobile());

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsMobile(checkIsMobile());
    mq.addEventListener('change', handler);
    window.addEventListener('orientationchange', handler);
    window.addEventListener('resize', handler);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMobile(checkIsMobile());
    return () => {
      mq.removeEventListener('change', handler);
      window.removeEventListener('orientationchange', handler);
      window.removeEventListener('resize', handler);
    };
  }, []);

  return isMobile;
}
